import { Component } from 'preact';
import style from './styles/RegistrationArea.scss';
import CountdownTimer from '../../countdown/CountdownTimer';
import CountdownTimerRenderer from '../../countdown/CountdownTimerRenderer';
import DetailCountdownTimerStyle from '../../countdown/styles/DetailCountdownTimer.scss';
import ButtonLink from '../../button/ButtonLink';
import RouteHelper from '../../../helpers/RouteHelper';
import RadioSelect from '../../form/RadioSelect';
import RaffleSizeSelect from '../../form/RaffleSizeSelect';
import Sexes from '../../../enums/Sexes';
import DateRenderer from './DateRenderer';
import DirectButtonLink from '../../button/DirectButtonLink';

/**
 * @property {?Raffle}         props.raffle       Raffle
 * @property {?string}         state.sex          Currently selected sex
 * @property {?string}         state.size         Currently selected size
 * @property {?SelectOption[]} state.sexOptions   Available sex options
 * @property {?RaffleSize[]}   state.sizeOptions  Available size options
 */
export default class RegistrationAreaContent extends Component {

	/**
	 * When sex changes, list of available size option must be updated
	 *
	 * @param {string} sex
	 */
	selectSexOption = (sex) => {
		const raffle = this.props.raffle;
		this.setState({
			sex,
			size: null,
			sizeOptions: raffle ? raffle.getSizeSetSizes(sex) : []
		});
	};

	/**
	 * @param {string} name
	 * @param {SelectChangeActionData} data
	 */
	onSexChange = (name, data) => {
		this.selectSexOption(data.value);
	};

	/**
	 * @param {string} size Size id
	 */
	onSizeChange = (size) => {
		this.setState({ size });
	};

	/**
	 * @param {Raffle} raffle
	 */
	createDefaultStateFromRaffle(raffle) {
		const availableSexes = raffle.getAvailableSexes();
		const defaultSex = raffle.getDefaultSex();

		return {
			sex: defaultSex,
			size: null,
			sexOptions: Sexes.toOptions().filter((option) => availableSexes.includes(option.value)),
			sizeOptions: raffle.getSizeSetSizes(defaultSex)
		};
	}

	constructor(props) {
		super(props);
		if (props.raffle) {
			// If we have a raffle, we use it to setup default state
			this.state = this.createDefaultStateFromRaffle(props.raffle);
		}
		else {
			// Default state
			this.state = {
				sex: null,
				size: null,
				sexOptions: [],
				sizeOptions: []
			};
		}
	}

	/**
	 * Get default sex
	 *
	 * @param nextProps
	 * @param nextContext
	 */
	componentWillReceiveProps(nextProps, nextContext) {
		// If raffle did change, update size and sex options
		if (nextProps.raffle !== this.props.raffle) {
			this.setState(this.createDefaultStateFromRaffle(nextProps.raffle));
		}
	}

	/**
	 * Make sure we are not re-rendering needlessly when our parent is "scrolling"
	 *
	 * @param nextProps
	 * @param nextState
	 * @param nextContext
	 * @return {boolean}
	 */
	shouldComponentUpdate(nextProps, nextState, nextContext) {

		const propsChanged = this.props.raffle !== nextProps.raffle;
		const stateChanged = (
			this.state.sex !== nextState.sex ||
			this.state.size !== nextState.size ||
			this.state.sexOptions !== nextState.sexOptions ||
			this.state.sizeOptions !== nextState.sizeOptions
		);
		return propsChanged || stateChanged;
	}

	render({ raffle, suggestedShopId }, { sex, size, sexOptions, sizeOptions }) {
		if (!raffle || !sex) {
			return null;
		}

		const { buttonText, countdownSection, showLinkToFootshop, showForm } = ((raffle) => {
			if (raffle.isActive()) {
				return {
					buttonText: 'Enter raffle now',
					countdownSection: (
						<div className={style['top-section']}>
							<h3>Raffle registration ends in</h3>
							<CountdownTimer
								renderer={CountdownTimerRenderer}
								style={DetailCountdownTimerStyle}
								date={raffle.getCloseRegistrationAtAsDate()}
								withDividers
							/>
						</div>
					),
					showLinkToFootshop: false,
					showForm: true
				};
			}
			else if (raffle.isComingSoon()) {
				return {
					buttonText: 'Raffle is not open yet',
					countdownSection: (
						<div className={style['top-section']}>
							<h3>Raffle registration opens in</h3>
							<CountdownTimer
								renderer={CountdownTimerRenderer}
								style={DetailCountdownTimerStyle}
								date={raffle.getOpenRegistrationAtAsDate()}
								withDividers
							/>
						</div>
					),
					showLinkToFootshop: false,
					showForm: true
				};
			}

			if (raffle.showLinkToFootshop()) {
				return {
					buttonText: 'Launched on',
					countdownSection: (
						<div className={style['top-section']}>
							<h3>Launched on</h3>
							<DateRenderer date={raffle.getCloseRegistrationAtAsDate()} />
						</div>
					),
					showLinkToFootshop: true,
					showForm: false
				};
			}

			return {
				buttonText: 'Raffle has ended',
				countdownSection: (
					<div className={style['top-section']}>
						<h3>Raffle registration has ended</h3>
					</div>
				),
				showLinkToFootshop: false,
				showForm: false
			};
		})(raffle);

		const buttonDisabled = !size || !raffle.isActive();
		const directButtonText = raffle.soldout ? 'Shop other exclusives' : 'Shop on footshop';

		const shopUrl = RouteHelper.getExternalUrlByShopId(suggestedShopId);

		const appOnly = raffle.applicationOnly;

		return (
			<div>
				{countdownSection}
				{appOnly && (
					<div className={style['bottom-section']}>
						<h4 className={style.appHeadline}>Available only in our app</h4>
						<div className={style.appBtns}>
							<a href="https://apps.apple.com/app/com.footshop.ftshp/id1667088153">
								<img src="https://static.ftshp.digital/themes/default-bootstrap/dist/images/pages/app-landing-page/app-store.svg" alt="Get it on App Store"/>
							</a>
							<a href="https://play.google.com/store/apps/details?id=com.footshop.ftshp">
								<img src="https://static.ftshp.digital/themes/default-bootstrap/dist/images/pages/app-landing-page/google-play-badge.png" alt="Get it on Google Play"/>
							</a>
						</div>
						<div className={style.qr}>
							<img src="https://static.ftshp.digital/themes/default-bootstrap/dist/images/pages/app-landing-page/qr-download-ftshp-app-black.svg" alt="Get it by QR"/>
							<p>Scan our QR code and don't miss the chance to have this limited piece in your shoe cabinet.</p>
						</div>
					</div>
				)}
				{!appOnly && (showLinkToFootshop ? (
					<div className={style['bottom-section']}>
						<div className="text-center">
							<DirectButtonLink href={raffle.footshopLink} text={directButtonText} className={style['direct-button-link']} showArrow />
						</div>
					</div>
				) : showForm && (
					<div className={style['bottom-section']}>
						<div className="mt-3">
							<RadioSelect options={sexOptions} name="sex" value={sex} onChangeAction={this.onSexChange} className="custom-control-inline " />
						</div>
						<div className="mt-4">
							<RaffleSizeSelect sizes={sizeOptions} name="size" value={size} text="Choose your size" onChange={this.onSizeChange} />
						</div>
						<div>
							<ButtonLink text={buttonText} disabled={buttonDisabled} to={RouteHelper.getUrl('/register/' + raffle.id + '/' + sex + '/' + size)} showArrow className={style['enter-raffle']} />
						</div>
						<div className={'mt-5'}>
							<a
								href={`${shopUrl}profile/login#register`}
								className={style.promoLink}
								target={'_blank'}
								rel={'noopener'}
							>
								2&times; the chance of winning thanks to Footshop Club registration. 10&times; the chance if you become VIP member.
							</a>
						</div>
					</div>
				))}
			</div>
		);
	}
}



// WEBPACK FOOTER //
// ./components/blocks/raffleDetail/RegistrationAreaContent.js